import { useCallback, useEffect, useRef, useState } from 'react';
import { standardReportsService } from '@/services/standardReports.service';
import { getErrorMessage } from '@/services/apiClient';
import {
  isTerminalRunStatus,
  type StandardReportParams,
  type StandardReportRun,
  type StandardReportRunStatus,
} from '@/types/standardReports.types';

/**
 * Queues a run and polls it to completion.
 *
 * Hides the queue from the pages entirely. Every run is queued — there is no
 * synchronous path — so without this each page would reimplement the same
 * poll loop, and they would drift on the details that matter: stopping on
 * every terminal state rather than only on success, and stopping when the
 * component goes away.
 */

const POLL_INTERVAL_MS = 2000;

/**
 * A ceiling on polling, not on the run. The worker's own timeout is under five
 * minutes, so a run still pending well past that is not going to arrive — and
 * a page left open overnight should not keep asking forever.
 */
const MAX_POLL_MS = 6 * 60 * 1000;

interface State {
  run: StandardReportRun | null;
  status: StandardReportRunStatus | null;
  running: boolean;
  error: string | null;
  timedOut: boolean;
}

export function useStandardReportRun(reportKey: string) {
  const [state, setState] = useState<State>({
    run: null,
    status: null,
    running: false,
    error: null,
    timedOut: false,
  });

  // Survives re-renders and is cleared on unmount, so a poll can never
  // outlive the page that started it or call setState afterwards.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelled = useRef(false);

  const stopPolling = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => {
    cancelled.current = false;

    return () => {
      cancelled.current = true;
      stopPolling();
    };
  }, [stopPolling]);

  const poll = useCallback(
    (runId: number, startedAt: number) => {
      timer.current = setTimeout(async () => {
        if (cancelled.current) return;

        try {
          const status = await standardReportsService.getRunStatus(runId);
          if (cancelled.current) return;

          setState((s) => ({ ...s, status: status.status }));

          // Stops on EVERY terminal state, not just `ready`. An `empty` or
          // `too_large` run is a finished answer; treating those as "still
          // working" would spin forever on a run that is already done.
          if (isTerminalRunStatus(status.status)) {
            const run = await standardReportsService.getRun(runId);
            if (cancelled.current) return;

            setState({
              run,
              status: status.status,
              running: false,
              error: null,
              timedOut: false,
            });
            return;
          }

          if (Date.now() - startedAt > MAX_POLL_MS) {
            setState((s) => ({ ...s, running: false, timedOut: true }));
            return;
          }

          poll(runId, startedAt);
        } catch (e) {
          if (cancelled.current) return;

          setState((s) => ({
            ...s,
            running: false,
            error: getErrorMessage(e, 'Lost contact with the report while it was running.'),
          }));
        }
      }, POLL_INTERVAL_MS);
    },
    [],
  );

  const start = useCallback(
    async (params: StandardReportParams) => {
      stopPolling();
      setState({ run: null, status: null, running: true, error: null, timedOut: false });

      try {
        const run = await standardReportsService.run(reportKey, params);
        if (cancelled.current) return;

        setState((s) => ({ ...s, run, status: run.status }));
        poll(run.runId, Date.now());
      } catch (e) {
        if (cancelled.current) return;

        setState({
          run: null,
          status: null,
          running: false,
          error: getErrorMessage(e, 'Could not start the report.'),
          timedOut: false,
        });
      }
    },
    [reportKey, poll, stopPolling],
  );

  const reset = useCallback(() => {
    stopPolling();
    setState({ run: null, status: null, running: false, error: null, timedOut: false });
  }, [stopPolling]);

  return { ...state, start, reset };
}
